import React from "react";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import { useNavigate } from "react-router-dom";
import StatCard from "../components/dashboard/StatCard";
import SalesChart, { ChartPeriod } from "../components/dashboard/SalesChart";
import RecentBookings from "../components/dashboard/RecentBookings";
import TopStylists from "../components/dashboard/TopStylists";
import QuickActionsPanel from "../components/dashboard/QuickActionsPanel";
import MyChairToday, { buildChairRowsFromBookings } from "../components/dashboard/MyChairToday";
import {
  PoundSterling,
  Calendar,
  Clock,
  UserCheck,
} from "lucide-react";
import {
  getDashboard,
  DashboardResponse,
  getUpcomingBookings,
  getBookingServicesLabel,
  DashboardChartPeriod,
} from "../services/dashboard";
import UpcomingBookings from "../components/dashboard/UpCominBookings";
import AppointmentSidebar from "../components/AppointmentSidebar";
import GlobalLoader from "../components/ui/GlobalLoader";
import { useToast } from "../hooks/useToast";
import { useEmployees } from "../hooks/useEmployees";
import { useAuth } from "../auth/AuthContext";
import { formatNextAppointmentLabel, toDateInputValue } from "../lib/dashboardUtils";

const getWeekRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - end.getDay());
  return {
    startDate: toDateInputValue(start),
    endDate: toDateInputValue(end),
  };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClaimed = user?.company?.is_claim === "yes";

  React.useEffect(() => {
    const isEmployee = user?.roles?.some((role: any) => role?.name === "employee");
    if (isEmployee) {
      navigate("/employee/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const toast = useToast();
  const [dashboard, setDashboard] = React.useState<DashboardResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [chartPeriod, setChartPeriod] = React.useState<ChartPeriod>("week");
  const [dateRange, setDateRange] = React.useState(getWeekRange);
  const [isAppointmentSidebarOpen, setIsAppointmentSidebarOpen] = React.useState(false);
  const [appointmentSidebarMode, setAppointmentSidebarMode] = React.useState<"Services" | "Sunbeds">("Services");
  const [upcomingBookings, setUpcomingBookings] = React.useState<Awaited<ReturnType<typeof getUpcomingBookings>>["bookings"]>([]);
  const [todayChairBookings, setTodayChairBookings] = React.useState<Awaited<ReturnType<typeof getUpcomingBookings>>["bookings"]>([]);
  const [upcomingLoading, setUpcomingLoading] = React.useState(false);
  const [selectedUpcomingDate, setSelectedUpcomingDate] = React.useState<Date>(new Date());

  const { staffList } = useEmployees();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "";

  const handleBookingClick = (bookingId: string | number) => {
    navigate(`/summary/bookings/${bookingId}`);
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case "Add Sale":
        setAppointmentSidebarMode("Services");
        setIsAppointmentSidebarOpen(true);
        break;
      case "Add Sunbed Sale":
        setAppointmentSidebarMode("Sunbeds");
        setIsAppointmentSidebarOpen(true);
        break;
      case "POS":
        navigate("/pos");
        break;
      case "Calendar":
        navigate("/calendar");
        break;
      case "Bookings":
        navigate("/bookings");
        break;
      case "Services":
        navigate("/manage/services");
        break;
      case "Products":
        navigate("/manage/products");
        break;
      case "Employees":
        navigate("/manage/employee");
        break;
      default:
        break;
    }
  };

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getDashboard(
          dateRange.startDate,
          dateRange.endDate,
          chartPeriod as DashboardChartPeriod
        );
        setDashboard(res);
      } catch (err) {
        console.error("Dashboard failed:", err);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    })();
  }, [dateRange, chartPeriod]);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await getUpcomingBookings(toDateInputValue(new Date()));
        setTodayChairBookings(res.bookings);
      } catch (err) {
        console.error(err);
        setTodayChairBookings([]);
      }
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      setUpcomingLoading(true);
      try {
        const dateStr = toDateInputValue(selectedUpcomingDate);
        const res = await getUpcomingBookings(dateStr);
        setUpcomingBookings(res.bookings);
      } catch (err) {
        console.error(err);
        setUpcomingBookings([]);
      } finally {
        setUpcomingLoading(false);
      }
    })();
  }, [selectedUpcomingDate]);

  const todayStats = dashboard?.todayStats;
  const chairRows = React.useMemo(
    () => buildChairRowsFromBookings(todayChairBookings),
    [todayChairBookings]
  );

  return (
    <div className="min-h-screen w-full bg-[#F9F9F9] text-[#1E1E1E] font-inter p-2 md:py-6 md:px-3 border">
      <div className="px-2 md:px-3 space-y-8">
        <DashboardHeader
          onQuickAction={handleQuickAction}
          onDateChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          currencySymbol={currencySymbol}
        />

        {loading ? (
          <GlobalLoader initialPageLoad={true} description="Loading dashboard statistics..." />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={PoundSterling}
                label="Today's Revenue"
                value={`${currencySymbol}${(todayStats?.revenue ?? 0).toFixed(2)}`}
              />
              <StatCard
                icon={Calendar}
                label="Today's Bookings"
                value={todayStats?.bookings ?? 0}
              />
              <StatCard
                icon={Clock}
                label="Next Appointment"
                value={todayStats?.nextAppointment?.time ?? "None"}
                hint={formatNextAppointmentLabel(todayStats?.nextAppointment ?? null)}
              />
              <StatCard
                icon={UserCheck}
                label="Walk-ins Today"
                value={todayStats?.walkIns ?? 0}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              <div className="space-y-6 h-full flex flex-col">
                <QuickActionsPanel isClaimed={isClaimed} onAction={handleQuickAction} />
                <div className="flex-1">
                  <SalesChart
                    labels={dashboard?.topSaleLabels ?? []}
                    data={dashboard?.topSaleData ?? []}
                    currencySymbol={currencySymbol}
                    period={chartPeriod}
                    onPeriodChange={setChartPeriod}
                  />
                </div>
              </div>

              <div className="h-full flex flex-col">
                <UpcomingBookings
                  currencySymbol={currencySymbol}
                  bookings={upcomingBookings}
                  loading={upcomingLoading}
                  selectedDate={selectedUpcomingDate}
                  onDateChange={setSelectedUpcomingDate}
                  onBookingClick={handleBookingClick}
                />
              </div>
            </div>

            <MyChairToday
              chairs={chairRows}
              loading={false}
              currencySymbol={currencySymbol}
              onBookingClick={handleBookingClick}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RecentBookings
                currencySymbol={currencySymbol}
                bookings={
                  dashboard?.recentSales.map((sale) => {
                    const staffName = sale.items?.[0]?.employee?.name ?? "No Staff Assigned";
                    const time = sale.date_time
                      ? new Date(sale.date_time).toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "N/A";

                    return {
                      id: sale.id,
                      customer: sale?.user?.name || "N/A",
                      email: sale.user?.email || "N/A",
                      mobile: sale.user?.formatted_mobile || sale.user?.mobile || "N/A",
                      staff: staffName,
                      services: getBookingServicesLabel(sale.items),
                      date: new Date(sale.date_time).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }),
                      time,
                      status: sale.status,
                      amount: `${currencySymbol}${Number(sale.amount_to_pay).toFixed(2)}`,
                    };
                  }) ?? []
                }
              />
              <TopStylists
                stylists={
                  dashboard?.stylistsChartData.labels.map((name, i) => ({
                    name,
                    thisMonth: dashboard.stylistsChartData.earningsData[i] ?? 0,
                    total: dashboard.stylistsChartData.earningsData[i] ?? 0,
                  })) ?? []
                }
                currencySymbol={currencySymbol}
              />
            </div>
          </>
        )}
      </div>

      <AppointmentSidebar
        isOpen={isAppointmentSidebarOpen}
        onClose={() => {
          setIsAppointmentSidebarOpen(false);
          setAppointmentSidebarMode("Services");
        }}
        staffList={staffList}
        initialServiceType={appointmentSidebarMode}
        mode="dashboard"
      />
    </div>
  );
};

export default Dashboard;
